import React, { useState, useMemo } from 'react';
import { Search, Calculator } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import { stonesTotalGrams } from '../../utils/calculations';
import {
  calculateFineWeight,
  getGoldPurityFactor,
  calculateAcquisitionCost,
  calculateCertificationCharges,
  calculateWastageAmount,
  calculateMakingAmount,
  calculateTotalGoldAmount,
} from '../../shared/utils/jewelryCalculations';

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const wt = (n) => `${(Number(n) || 0).toFixed(3)}g`;
const unitLabel = (u) => {
  const x = (u || '').toLowerCase();
  if (x.startsWith('carat') || x === 'ct') return 'Carat';
  if (x.startsWith('gram') || x === 'g') return 'Grams';
  if (x.startsWith('number') || x === 'pcs' || x === 'nos') return 'Numbers';
  return u || '';
};

const numInput = (value, onChange, width = 'w-20') => (
  <input
    type="number"
    value={value}
    onChange={e => onChange(e.target.value)}
    className={`${width} border border-amber-300 rounded px-1.5 py-0.5 text-xs font-mono text-right bg-white focus:outline-none focus:ring-1 focus:ring-amber-500`}
  />
);

const CostBreakdown = () => {
  const [itemCode, setItemCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [stoneLines, setStoneLines] = useState([]);
  const [wastagePercent, setWastagePercent] = useState('');
  const [makingRate, setMakingRate] = useState('');
  const [targetMargin, setTargetMargin] = useState('15');

  const lookup = async () => {
    const code = itemCode.trim().toUpperCase();
    if (!code) return;
    setLoading(true);
    setError('');
    setItem(null);
    setStoneLines([]);

    try {
      const itemRes = await fetch(`/api/jewelry/details/${code}`, { credentials: 'include' });
      const data = await itemRes.json();
      if (!data || data.code !== code) {
        setError('Item not found.');
        setLoading(false);
        return;
      }

      const categoryCode = code.split('-')[0];
      const [categoriesRes, goldRateRes, diamondRes, flatDiamondRes] = await Promise.all([
        fetch('/api/categories', { credentials: 'include' }),
        fetch('/api/rates/gold/today', { credentials: 'include' }),
        fetch('/api/materials/category/Diamond', { credentials: 'include' }),
        fetch('/api/materials/category/Flat Diamond', { credentials: 'include' }),
      ]);
      const categoriesData = await categoriesRes.json();
      const goldRateData = await goldRateRes.json();
      const diamondData = await diamondRes.json();
      const flatDiamondData = await flatDiamondRes.json();

      const categories = Array.isArray(categoriesData) ? categoriesData : (categoriesData.categories || []);
      const category = categories.find(c => c.code === categoryCode)
        || categories.find(c => c.name?.toUpperCase().includes(categoryCode));
      const parent = category?.parent_id ? categories.find(c => c.id === category.parent_id) : null;
      const isKundan = !!(category && (category.id === 4 || category.parent_id === 4));

      const goldRate = parseFloat(goldRateData?.rate?.gold_24k) || 0;
      const purity = data.gold_purity || '';
      const purityFactor = getGoldPurityFactor(purity);
      const grossWeight = parseFloat(data.gross_weight) || 0;
      const netWeight = parseFloat(data.net_weight) || 0;
      const stones = data.stones || [];
      const stoneWeight = stonesTotalGrams(stones);
      const finePercent = data.fine_percentage ? parseFloat(data.fine_percentage) : null;
      const kundanWeight = data.kundan_weight ? parseFloat(data.kundan_weight) : null;
      const kundanPercent = data.kundan_percentage ? parseFloat(data.kundan_percentage) : null;
      const laborCharges = data.labor_charges ? parseFloat(data.labor_charges) : 0;

      const diamondCodes = [
        ...(Array.isArray(diamondData) ? diamondData : []),
        ...(Array.isArray(flatDiamondData) ? flatDiamondData : []),
      ].map(m => m.code);
      const diamondCarats = stones
        .filter(s => diamondCodes.includes(s.stone_code))
        .reduce((sum, s) => sum + (parseFloat(s.weight) || 0), 0);
      const certChargePerCarat = parseFloat(category?.certification_charges) || 0;
      const certificateRequired = data.certificate === 'Yes';

      setItem({
        code: data.code, categoryName: category?.name || categoryCode, isKundan,
        purity, purityFactor, grossWeight, netWeight, stoneWeight, finePercent, kundanWeight, kundanPercent,
        goldRate, laborCharges, diamondCarats, certChargePerCarat, certificateRequired, stones,
      });

      setStoneLines(stones.map(s => ({
        code: s.stone_code,
        name: s.stone_name || s.stone_code,
        weight: parseFloat(s.weight) || 0,
        unit: unitLabel(s.unit),
        costPrice: parseFloat(s.cost_price) || 0,
        salePrice: parseFloat(s.sale_price) || 0,
      })));

      const effectiveWastage = parseFloat(category?.sale_wastage_pct) > 0
        ? category.sale_wastage_pct
        : (parent ? parent.sale_wastage_pct : 0);
      const effectiveMaking = parseFloat(category?.sale_making_per_g) > 0
        ? category.sale_making_per_g
        : (parent ? parent.sale_making_per_g : 0);
      setWastagePercent(String(effectiveWastage || 0));
      setMakingRate(String(effectiveMaking || 0));
    } catch (err) {
      setError('Failed to load item cost breakdown.');
    } finally {
      setLoading(false);
    }
  };

  const updateStoneSalePrice = (index, value) => {
    setStoneLines(prev => prev.map((l, i) => i === index ? { ...l, salePrice: parseFloat(value) || 0 } : l));
  };

  const calc = useMemo(() => {
    if (!item) return null;
    const { grossWeight, netWeight, stoneWeight, finePercent, kundanWeight, kundanPercent,
      goldRate, purity, purityFactor, laborCharges, diamondCarats, certChargePerCarat, certificateRequired } = item;

    const wastage = parseFloat(wastagePercent) || 0;
    const making = parseFloat(makingRate) || 0;

    const costSteps = [];
    const kw = kundanWeight || 0;
    const kp = kundanPercent || 0;
    const baseGoldWeight = grossWeight - stoneWeight - kw;
    costSteps.push({
      title: '1. Stone Weight',
      explanation: `Every stone's weight converted to grams: Carat weight × 0.2 (1 carat = 0.2g), Gram weight used as-is, and piece-count stones (Numbers) contribute 0g since they're not measured by weight.`,
      formula: item.stones.length > 0
        ? item.stones.map(s => `${s.stone_code}: ${s.weight} ${unitLabel(s.unit)}`).join(', ') + ` → total ${wt(stoneWeight)}`
        : 'No stones on this item → 0g',
      result: wt(stoneWeight),
    });
    costSteps.push({
      title: '2. Base Gold Weight',
      explanation: `Gross Weight minus Stone Weight minus Kundan Weight — the portion of the item that's plain gold, separate from the stones and the Kundan (foil/mount) gold.`,
      formula: `${wt(grossWeight)} − ${wt(stoneWeight)} − ${wt(kw)} = ${wt(baseGoldWeight)}`,
      result: wt(baseGoldWeight),
    });
    const fineWeight = finePercent > 0
      ? (baseGoldWeight * (finePercent / 100)) + (kw * (kp / 100))
      : calculateFineWeight(netWeight, purity);
    costSteps.push({
      title: '3. Fine Weight',
      explanation: finePercent > 0
        ? `This item has a Fine % recorded. The base gold portion and the Kundan portion are assayed at different purities, so each is converted to fine gold separately and then added: (Base Gold Weight × Fine%) + (Kundan Weight × Kundan%).`
        : `No Fine % recorded, so fine weight uses the standard purity factor for ${purity || 'this purity'} (${purityFactor}) on Net Weight.`,
      formula: finePercent > 0
        ? `(${wt(baseGoldWeight)} × ${finePercent}%) + (${wt(kw)} × ${kp}%) = ${wt(baseGoldWeight * (finePercent / 100))} + ${wt(kw * (kp / 100))} = ${wt(fineWeight)}`
        : `${wt(netWeight)} × ${purityFactor} = ${wt(fineWeight)}`,
      result: wt(fineWeight),
    });
    const goldCost = Math.ceil(fineWeight * goldRate);
    costSteps.push({
      title: '4. Gold Cost',
      explanation: `Fine Weight × today's 24K gold rate, rounded up to the nearest rupee. Fine Weight already accounts for purity/Kundan assay, so no further purity adjustment is needed here.`,
      formula: `${wt(fineWeight)} × ₹${goldRate.toLocaleString('en-IN')}/g = ${fmt(fineWeight * goldRate)} → rounded up`,
      result: fmt(goldCost),
    });
    const stoneCostRaw = stoneLines.reduce((sum, l) => sum + (l.weight * l.costPrice), 0);
    const stoneCost = Math.ceil(stoneCostRaw);
    costSteps.push({
      title: '5. Stone Cost',
      explanation: stoneLines.length > 0
        ? `Sum of each stone's weight × its purchase cost price (not the customer-facing sale price), rounded up.`
        : `No stones recorded on this item, so stone cost is ₹0.`,
      formula: `Sum of line costs below = ${fmt(stoneCostRaw)} → rounded up`,
      result: fmt(stoneCost),
      table: stoneLines.length > 0
        ? { priceLabel: 'Cost Price', lines: stoneLines.map(l => ({ code: l.code, weight: l.weight, unit: l.unit, price: l.costPrice, lineTotal: l.weight * l.costPrice })) }
        : null,
    });
    const laborCost = Math.ceil(netWeight * laborCharges);
    costSteps.push({
      title: '6. Labor Cost',
      explanation: `Net Weight × the labor rate recorded on this item at entry, rounded up.`,
      formula: `${wt(netWeight)} × ₹${laborCharges}/g = ${fmt(netWeight * laborCharges)} → rounded up`,
      result: fmt(laborCost),
    });
    const certCost = calculateCertificationCharges(diamondCarats, certChargePerCarat, certificateRequired);
    costSteps.push({
      title: '7. Certification Cost',
      explanation: certificateRequired
        ? `Certificate required on this item. Diamond carats are the sum of every stone whose material code is tagged "Diamond" or "Flat Diamond" (${diamondCarats.toFixed(3)}ct here), × the category's certification charge per carat.`
        : `No certificate required on this item, so certification cost is ₹0.`,
      formula: certificateRequired ? `${diamondCarats.toFixed(3)}ct × ₹${certChargePerCarat}/ct = ${fmt(certCost)}` : '—',
      result: fmt(certCost),
    });
    const totalCost = calculateAcquisitionCost({ goldCost, stoneCost, laborCost, certCost });
    costSteps.push({
      title: '8. Actual Cost Price',
      explanation: `Sum of Gold Cost + Stone Cost + Labor Cost + Certification Cost — this is what the item cost the shop to acquire. Tax is not included here; it's applied in Estimates/Billing.`,
      formula: `${fmt(goldCost)} + ${fmt(stoneCost)} + ${fmt(laborCost)} + ${fmt(certCost)} = ${fmt(totalCost)}`,
      result: fmt(totalCost), bold: true,
    });

    // ── Sale Price (customer-facing) ──
    const saleSteps = [];
    const goldPricePerGram = goldRate * purityFactor;
    const saleBaseWeight = Math.max(0, netWeight - kw);
    // Kundan is priced at the full (undiscounted) gold rate, not the standard purity
    // rate — so its weight-equivalent at standard rate is simply Kundan Weight ÷ Purity
    // Factor (a smaller purity factor = a bigger equivalent weight, since it's priced
    // higher). Adding that to the base weight gives one Gold Weight for the whole item.
    const kundanEquivalentWeight = purityFactor > 0 ? kw / purityFactor : kw;
    const goldWeight = saleBaseWeight + kundanEquivalentWeight;
    const goldValue = goldWeight * goldPricePerGram;
    saleSteps.push({
      title: '11. Gold Weight & Value (Sale)',
      explanation: kw > 0
        ? `Kundan gold is priced at the full rate, not the standard ${purity} rate, so it's converted to an equivalent weight (÷ Purity Factor) and added to the base weight — giving one Gold Weight for the printed tag/bill.`
        : `Net Weight × standard Purity-adjusted Rate (no Kundan Weight on this item).`,
      formula: kw > 0
        ? `Gold Weight = (${wt(netWeight)} − ${wt(kw)}) + (${wt(kw)} ÷ ${purityFactor}) = ${wt(saleBaseWeight)} + ${wt(kundanEquivalentWeight)} = ${wt(goldWeight)}\n${wt(goldWeight)} × ₹${goldPricePerGram.toFixed(2)}/g = ${fmt(goldValue)}`
        : `${wt(netWeight)} × ₹${goldPricePerGram.toFixed(2)}/g = ${fmt(goldValue)}`,
      result: fmt(goldValue),
    });
    const wastageAmount = calculateWastageAmount(netWeight, wastage, goldPricePerGram);
    saleSteps.push({
      title: '12. Wastage (VA)',
      explanation: `Value Addition % applied to net weight, converted to a gold-rate value, rounded up.`,
      formula: `CEIL(${wt(netWeight)} × ${wastage}%) × ₹${goldPricePerGram.toFixed(2)}/g = ${fmt(wastageAmount)}`,
      result: fmt(wastageAmount),
      editable: 'wastage',
    });
    const makingAmount = calculateMakingAmount(netWeight, making);
    saleSteps.push({
      title: '13. Making Charges',
      explanation: `Net Weight × the making rate per gram.`,
      formula: `${wt(netWeight)} × ₹${making}/g = ${fmt(makingAmount)}`,
      result: fmt(makingAmount),
      editable: 'making',
    });
    const totalGoldAmount = calculateTotalGoldAmount(goldValue, wastageAmount, makingAmount);
    saleSteps.push({
      title: '14. Total Gold Amount (Sale)',
      explanation: `Gold Value + Wastage + Making, rounded up.`,
      formula: `${fmt(goldValue)} + ${fmt(wastageAmount)} + ${fmt(makingAmount)} = ${fmt(totalGoldAmount)}`,
      result: fmt(totalGoldAmount),
    });
    const stoneSaleRaw = stoneLines.reduce((sum, l) => sum + (l.weight * l.salePrice), 0);
    const stoneSaleValue = Math.ceil(stoneSaleRaw);
    saleSteps.push({
      title: '15. Stone Sale Value',
      explanation: stoneLines.length > 0
        ? `Sum of each stone's weight × its editable selling price, rounded up.`
        : `No stones recorded on this item, so stone sale value is ₹0.`,
      formula: `Sum of line values below = ${fmt(stoneSaleRaw)} → rounded up`,
      result: fmt(stoneSaleValue),
      table: stoneLines.length > 0
        ? { priceLabel: 'Sell Price', lines: stoneLines.map(l => ({ code: l.code, weight: l.weight, unit: l.unit, price: l.salePrice, lineTotal: l.weight * l.salePrice })) }
        : null,
    });
    const salePrice = Math.ceil(totalGoldAmount) + Math.ceil(stoneSaleValue) + certCost;
    saleSteps.push({
      title: '16. Sale Price',
      explanation: `Total Gold Amount + Stone Sale Value + Certification Cost (same certification charge as the cost side — it's passed through, not marked up). Tax is not included here; it's applied in Estimates/Billing.`,
      formula: `${fmt(totalGoldAmount)} + ${fmt(stoneSaleValue)} + ${fmt(certCost)} = ${fmt(salePrice)}`,
      result: fmt(salePrice), bold: true,
    });

    const marginAmount = salePrice - totalCost;
    const marginPercent = totalCost > 0 ? (marginAmount / totalCost) * 100 : 0;

    // Suggested Making rate to hit the target margin, holding VA%, Sell Prices, and
    // everything else fixed. Approximate (ignores the ceil() rounding steps), so treat
    // as a starting point — the margin box above recalculates exactly as you type it in.
    const target = parseFloat(targetMargin) || 0;
    const desiredSalePrice = totalCost * (1 + target / 100);
    const desiredTotalGoldAmount = desiredSalePrice - stoneSaleValue - certCost;
    const desiredMakingAmount = desiredTotalGoldAmount - goldValue - wastageAmount;
    const suggestedMaking = netWeight > 0 ? Math.max(0, desiredMakingAmount / netWeight) : 0;

    return { costSteps, saleSteps, totalCost, salePrice, marginAmount, marginPercent, suggestedMaking };
  }, [item, stoneLines, wastagePercent, makingRate, targetMargin]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS?.ADMINISTRATION?.COST_BREAKDOWN || 'ADM-010'} pageName="Cost Breakdown" />
      <div className="flex items-center gap-2 mb-4">
        <Calculator className="text-amber-600" size={22} />
        <h1 className="text-xl font-bold text-slate-800">Cost Breakdown</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Enter an item code to see its weights and every step of how its actual acquisition cost and sale price (INR) are calculated. Sell Price, VA%, Making, and Customs/GST are editable.
      </p>

      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={itemCode}
          onChange={e => setItemCode(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') lookup(); }}
          placeholder="Item Code"
          className="flex-1 border-2 border-amber-300 rounded-lg px-3 py-2 text-sm font-mono font-semibold bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
        <button
          onClick={lookup}
          disabled={loading}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg text-sm"
        >
          <Search size={16} /> {loading ? 'Loading…' : 'Look Up'}
        </button>
      </div>

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      {item && calc && (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 items-start">
          <div className="lg:sticky lg:top-4 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-700 px-4 py-2 text-white text-sm font-semibold">
              <div>{item.code}</div>
              <div className="text-slate-300 text-xs">{item.categoryName}{item.isKundan ? ' (Kundan)' : ''} · {item.purity}</div>
            </div>

            <div className="bg-slate-50 px-4 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Weights</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Weight Field</th>
                  <th className="px-4 py-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="px-4 py-2 text-slate-600">Gross Weight</td><td className="px-4 py-2 text-right font-medium text-slate-800">{wt(item.grossWeight)}</td></tr>
                <tr><td className="px-4 py-2 text-slate-600">Net Weight</td><td className="px-4 py-2 text-right font-medium text-slate-800">{wt(item.netWeight)}</td></tr>
                <tr><td className="px-4 py-2 text-slate-600">Stone Weight</td><td className="px-4 py-2 text-right font-medium text-slate-800">{wt(item.stoneWeight)}</td></tr>
                {item.finePercent != null && (
                  <tr><td className="px-4 py-2 text-slate-600">Fine %</td><td className="px-4 py-2 text-right font-medium text-yellow-700">{item.finePercent.toFixed(2)}%</td></tr>
                )}
                {item.kundanWeight != null && (
                  <tr><td className="px-4 py-2 text-slate-600">Kundan Weight</td><td className="px-4 py-2 text-right font-medium text-amber-700">{wt(item.kundanWeight)}</td></tr>
                )}
                {item.kundanPercent != null && (
                  <tr><td className="px-4 py-2 text-slate-600">Kundan %</td><td className="px-4 py-2 text-right font-medium text-amber-700">{item.kundanPercent.toFixed(2)}%</td></tr>
                )}
                <tr><td className="px-4 py-2 text-slate-600">Gold Rate (today, 24K)</td><td className="px-4 py-2 text-right font-medium text-slate-800">₹{item.goldRate.toLocaleString('en-IN')}/g</td></tr>
              </tbody>
            </table>

            {stoneLines.length > 0 && (
              <>
                <div className="bg-slate-50 px-4 py-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider border-t border-slate-200">Stones</div>
                <table className="w-full text-xs table-fixed">
                  <colgroup>
                    <col className="w-[30%]" />
                    <col className="w-[20%]" />
                    <col className="w-[20%]" />
                    <col className="w-[30%]" />
                  </colgroup>
                  <thead>
                    <tr className="text-left text-slate-400 uppercase tracking-wide">
                      <th className="pl-4 py-1.5">Code</th>
                      <th className="py-1.5 text-right">Wt</th>
                      <th className="py-1.5 text-right">Cost</th>
                      <th className="pr-4 py-1.5 text-right">Sell</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {stoneLines.map((l, si) => (
                      <tr key={si}>
                        <td className="pl-4 py-1.5 font-medium text-slate-700 truncate" title={`${l.code} (${l.unit})`}>{l.code}</td>
                        <td className="py-1.5 text-right">{l.weight}</td>
                        <td className="py-1.5 text-right">₹{l.costPrice}</td>
                        <td className="pr-4 py-1.5 text-right">
                          {numInput(l.salePrice, (v) => updateStoneSalePrice(si, v), 'w-14')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Acquisition Cost</h2>
              <div className="space-y-3">
                {calc.costSteps.map((s, i) => (
                  <StepCard key={i} s={s} />
                ))}
              </div>
              <div className="bg-amber-50 border-2 border-amber-400 rounded-xl px-4 py-3 flex items-center justify-between mt-3">
                <span className="font-bold text-amber-800">Actual Cost Price</span>
                <span className="font-bold text-xl text-amber-800">{fmt(calc.totalCost)}</span>
              </div>
            </div>

            <div>
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">Sale Price</h2>
              <div className="space-y-3">
                {calc.saleSteps.map((s, i) => (
                  <StepCard key={i} s={s}>
                    {s.editable === 'wastage' && (
                      <span className="ml-2 inline-flex items-center gap-1">VA %: {numInput(wastagePercent, setWastagePercent)}</span>
                    )}
                    {s.editable === 'making' && (
                      <span className="ml-2 inline-flex items-center gap-1">₹/g: {numInput(makingRate, setMakingRate)}</span>
                    )}
                  </StepCard>
                ))}
              </div>
              <div className="bg-green-50 border-2 border-green-500 rounded-xl px-4 py-3 flex items-center justify-between mt-3">
                <span className="font-bold text-green-800">Sale Price</span>
                <span className="font-bold text-xl text-green-800">{fmt(calc.salePrice)}</span>
              </div>
            </div>
          </div>

          {(() => {
            const target = parseFloat(targetMargin) || 0;
            const hitTarget = calc.marginPercent >= target;
            return (
              <div className={`rounded-xl border-2 px-4 py-3 ${hitTarget ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-400'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`font-bold ${hitTarget ? 'text-green-800' : 'text-red-800'}`}>
                      Margin = Sale Price − Cost Price
                    </span>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Target Margin %: {numInput(targetMargin, setTargetMargin, 'w-14')} — {hitTarget ? 'target reached.' : 'below target.'}
                    </p>
                  </div>
                  <span className={`font-bold text-xl ${hitTarget ? 'text-green-800' : 'text-red-800'}`}>
                    {fmt(calc.marginAmount)} ({calc.marginPercent.toFixed(1)}%)
                  </span>
                </div>
                {!hitTarget && (
                  <div className="mt-2 pt-2 border-t border-red-200 flex items-center justify-between text-xs">
                    <span className="text-red-700">
                      To reach {target}% margin (keeping VA%, Sell Prices as-is), Making needs to be ≈ ₹{calc.suggestedMaking.toFixed(0)}/g (currently ₹{makingRate}/g).
                    </span>
                    <button
                      onClick={() => setMakingRate(calc.suggestedMaking.toFixed(0))}
                      className="ml-3 shrink-0 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-1 rounded"
                    >
                      Use ₹{calc.suggestedMaking.toFixed(0)}/g
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const StepCard = ({ s, children }) => (
  <div className={`bg-white rounded-xl border shadow-sm p-4 ${s.bold ? 'border-amber-400' : 'border-slate-200'}`}>
    <div className="flex items-center justify-between mb-1">
      <span className={`font-semibold ${s.bold ? 'text-amber-800' : 'text-slate-800'}`}>{s.title}</span>
      <span className={`font-bold ${s.bold ? 'text-amber-800 text-lg' : 'text-slate-800'}`}>{s.result}</span>
    </div>
    <p className="text-xs text-slate-500 mb-2">{s.explanation}{children}</p>
    <p className="text-xs font-mono bg-slate-50 rounded px-2 py-1.5 text-slate-600 break-words">{s.formula}</p>

    {s.table && (
      <table className="w-full text-xs mt-3 border-t border-slate-100 pt-2">
        <thead>
          <tr className="text-left text-slate-400 uppercase tracking-wide">
            <th className="py-1 pr-2">Code</th>
            <th className="py-1 pr-2 text-right">Weight</th>
            <th className="py-1 pr-2">Unit</th>
            <th className="py-1 pr-2 text-right">{s.table.priceLabel}</th>
            <th className="py-1 text-right">Line Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {s.table.lines.map((l, li) => (
            <tr key={li}>
              <td className="py-1 pr-2 font-medium text-slate-700">{l.code}</td>
              <td className="py-1 pr-2 text-right">{l.weight}</td>
              <td className="py-1 pr-2">{l.unit}</td>
              <td className="py-1 pr-2 text-right">₹{l.price}</td>
              <td className="py-1 text-right font-medium">{fmt(l.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default CostBreakdown;
